/* import __COLOCATED_TEMPLATE__ from './sq-field-radio-set.hbs'; */
/**
## Field Radio Set

`SqFieldRadioSetComponent` is a variation on `sq-option-set` that renders
an [sq-option-radio](./sq-option-radio.html) component for each option passed
to it's `options` parameter. Options should be contstructed in the following format:

```
{
  label: 'My Radio Option',
  labelSecondary: 'Some additional helpful text', //optional
  name: 'my-radio-option',
  value: 'radio-value',
  disabled: true/false, //default is false
  selected: true/false, //default is false
};

```
It extends the [Option Set](/components/dashboard/sq_option_set_component.html),
which emits an 'onChange' action when the selected option changes.

```example
@title Basic Example

@controller-properties
{
  sampleOptions: [
    {
      name: 'first',
      value: 0,
      label: 'First'
    },
    {
      name: 'first',
      value: 1,
      label: 'Second'
    },
    {
      name: 'first',
      value: 2,
      label: 'Third'
    },
    {
      name: 'first',
      value: 3,
      label: 'Fourth',
      labelSecondary: 'Extra text here for more detail.'
    },
    {
      name: 'first',
      value: 4,
      label: 'Disabled',
      disabled: true
    },
  ],
  sampleSelectedValue: 1,
  actions: {
    switchedOption: function(value) {
      $('.option-output').text('switched to ' + value);
    }
  }
}

@hbs
{{sq-field-radio-set value=sampleSelectedValue
                     options=sampleOptions
                     onChange=(action 'switchedOption')}}

<p class="option-output"></p>
```

```example
@title Example with custom radio format
@description You can use the contextual components to yield out custom formatted
radio options. For contextual radio options, you can access the properties of
the individual option within the yielded `radio` by using `radio.props` in
your template.

@controller-properties
{
  options: [
    {
      name: 'first',
      selected: false,
      label: 'First',
      someOtherPropertyName: 'This is the first one'
    },
    {
      name: 'second',
      selected: true,
      label: 'Second',
      someOtherPropertyName: 'This is the second one'
    }
  ]
}

@hbs
<h4>Basic Block</h4>
{{#sq-field-radio-set options=options as |radio|}}
  {{#radio.option}}
    <strong>{{radio.props.someOtherPropertyName}}</strong>
  {{/radio.option}}
{{/sq-field-radio-set}}

<h4>Contextual radio option components</h4>
{{#sq-field-radio-set options=options as |radio|}}
  {{#radio.option as |opt|}}
    {{opt.label text=radio.props.label}}
    {{#opt.label-secondary}}
      <strong>{{radio.props.someOtherPropertyName}}</strong>
    {{/opt.label-secondary}}
  {{/radio.option}}
{{/sq-field-radio-set}}
```

### Parameters
@param {String} name - A name for this set of radio options.
@param {Object} value - The currently selected value for the radio set. `null` or `undefined` will result in no options checked.
@param {Array} options - An array of option objects. Every object should have a `name` property, `value` property (the actual form value), and `label` (what's displayed). Optionally add `labelSecondary` for extra help text. Optionally add `disabled` for option level disabling.
@param {Boolean} [disabled=false] - Set to true to disable the entire radio set.
@param {Action} onChange - Called anytime the user switches options. Passes the new value as its parameter.
*/

import Component from '@ember/component';
import FieldElementSupport from '@square/glass-ui/utils/field-element-support';

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend(FieldElementSupport, {
  tagName: 'fieldset',
  classNames: ['option-set', 'radio-set'],
  options: null,
  // passed in

  actions: {
    /* You may notice that the parameters passed into this function are in a
    different order from those that are sent out in the onChange action.
    This is because the first param `option`, is passed in through this component's
    template, while the following 2 come directly from the `sq-radio` component.
    We pass `value` as the first param to the onChange action so that consuming
    components can use the (mut) template helper instead of needing to write a
    separate action to consume a second or third param */
    optionClicked(option, value, selected) {
      this.set('value', value);
      if (this.onChange) {
        this.onChange(value, option, selected);
      }
    }
  }
});